import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Feature, FeatureDepartment } from "../../../types/feature";
import { featureService } from "../../../services/featureService";
import {
  X, Settings, Globe, Building2, Plus, Trash2, ExternalLink,
  RefreshCw, Eye, EyeOff, Shield, Loader2, ChevronDown, AlertTriangle
} from "lucide-react";
import { BRAND_COLORS } from "../../../shared/constants/brandColors";

type DeptKey = "OMS" | "AMS" | "EPC";

const DEPT_STYLE: Record<DeptKey, { color: string; bg: string; border: string; activeBg: string; icon: string; fullName: string; cardBorder: string }> = {
  OMS: { color: "text-emerald-700", bg: "bg-emerald-50",  border: "border-emerald-200", activeBg: "bg-emerald-600", icon: "⚙️",  fullName: "Operations Management System", cardBorder: "border-emerald-200" },
  AMS: { color: "text-purple-700",  bg: "bg-purple-50",   border: "border-purple-200",  activeBg: "bg-purple-600",  icon: "🏗️", fullName: "Asset Management System", cardBorder: "border-purple-200" },
  EPC: { color: "text-orange-700",  bg: "bg-orange-50",   border: "border-orange-200",  activeBg: "bg-orange-600",  icon: "🔧", fullName: "Engineering, Procurement & Construction", cardBorder: "border-orange-200" },
};

type ConfirmState =
  | { type: "makeCommon"; key: string; name: string }
  | { type: "moveDept"; key: string; name: string }
  | { type: "delete"; key: string; name: string }
  | null;

interface Props {
  department: DeptKey;
  isOpen: boolean;
  onClose: () => void;
}

export default function DeptFeaturesDrawer({ department, isOpen, onClose }: Props) {
  const navigate = useNavigate();
  const style = DEPT_STYLE[department];

  const [deptFeatures, setDeptFeatures] = useState<Feature[]>([]);
  const [commonFeatures, setCommonFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [showCommon, setShowCommon] = useState(true);
  const [search, setSearch] = useState("");
  const [confirm, setConfirm] = useState<ConfirmState>(null);

  const load = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    try {
      const [deptAll, commonAll] = await Promise.all([
        featureService.getAllFeaturesForDept(department),
        featureService.getAllFeaturesForDept("COMMON"),
      ]);
      setDeptFeatures(deptAll);
      setCommonFeatures(commonAll);
    } catch {
      toast.error("Failed to load features");
    } finally {
      setLoading(false);
    }
  }, [isOpen, department]);

  useEffect(() => { load(); }, [load]);

  const runConfirmedAction = async () => {
    if (!confirm) return;
    setIsBusy(true);
    try {
      if (confirm.type === "makeCommon") {
        await featureService.bulkUpdateDepartment([confirm.key], "COMMON");
        toast.success("Feature moved to COMMON");
      } else if (confirm.type === "moveDept") {
        await featureService.bulkUpdateDepartment([confirm.key], department as FeatureDepartment);
        toast.success(`Feature moved to ${department}`);
      } else if (confirm.type === "delete") {
        await featureService.deleteFeature(confirm.key);
        toast.success("Feature deleted");
      }
      setConfirm(null);
      await load();
    } catch {
      toast.error("Action failed");
    } finally {
      setIsBusy(false);
    }
  };

  const handleToggleStatus = async (key: string) => {
    setIsBusy(true);
    try {
      await featureService.toggleFeatureStatus(key);
      await load();
    } catch { toast.error("Failed"); } finally { setIsBusy(false); }
  };

  const filteredDept = search
    ? deptFeatures.filter(f => f.name.toLowerCase().includes(search.toLowerCase()) || f.key.toLowerCase().includes(search.toLowerCase()))
    : deptFeatures;
  const filteredCommon = search
    ? commonFeatures.filter(f => f.name.toLowerCase().includes(search.toLowerCase()) || f.key.toLowerCase().includes(search.toLowerCase()))
    : commonFeatures;

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />

      <div className="fixed right-0 top-0 z-50 flex h-full w-full max-w-[720px] flex-col bg-white shadow-2xl">
        <div className={`px-5 py-4 border-b border-gray-200 ${style.bg}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl ${style.bg} border ${style.border}`}>
                {style.icon}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className={`text-base font-bold ${style.color}`}>{department} Features</h2>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${style.bg} ${style.color} border ${style.border}`}>
                    Catalog
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{style.fullName}</p>
                <p className="text-xs text-gray-400 mt-1">
                  <a
                    href="/admin/roles"
                    className="font-medium hover:underline"
                    style={{ color: BRAND_COLORS.primary }}
                    onClick={(e) => { e.preventDefault(); onClose(); navigate("/admin/roles"); }}
                  >
                    Role Management
                  </a>
                  <span className="text-gray-300"> · </span>
                  Quick edit; use Full View for bulk work.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={load} disabled={loading || isBusy} title="Refresh"
                className="p-1.5 rounded-lg hover:bg-white/70 text-gray-500 transition-colors">
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              </button>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/70 text-gray-500 transition-colors">
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3 mt-3 flex-wrap">
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${style.bg} ${style.color} border ${style.border}`}>
              <Building2 size={11} />
              {deptFeatures.length} exclusive
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
              <Globe size={11} />
              {commonFeatures.length} common (shared)
            </div>
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-50 text-gray-600 border border-gray-200">
              <Shield size={11} />
              {deptFeatures.length + commonFeatures.length} total visible
            </div>
          </div>
        </div>

        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[160px]">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">🔍</span>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search features…"
              className="w-full pl-7 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" />
          </div>
          <button
            onClick={() => setShowCommon(v => !v)}
            title={showCommon ? "Hide common features" : "Show common features"}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
              showCommon ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-gray-200 text-gray-500"
            }`}
          >
            {showCommon ? <Eye size={12} /> : <EyeOff size={12} />}
            Common
          </button>
          <button
            onClick={() => navigate(`/admin/feature-departments?dept=${department}`)}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${style.bg} ${style.color} ${style.border} hover:opacity-80`}
            title="Open full feature manager"
          >
            <ExternalLink size={12} />
            Full View
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={28} className="animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              <div className="px-4 pt-4 pb-2">
                <div className="flex items-center gap-2 mb-3">
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${style.bg} ${style.color} border ${style.border}`}>
                    <Building2 size={11} />
                    {department} Only Features
                  </div>
                  <div className="flex-1 border-t border-dashed" style={{ borderColor: 'rgba(0,0,0,0.1)' }} />
                  <span className="text-xs text-gray-400">{filteredDept.length}</span>
                </div>

                {filteredDept.length === 0 ? (
                  <div className="flex flex-col items-center py-8 text-gray-400 text-center">
                    <Building2 size={28} className="mb-2 opacity-40" />
                    <p className="text-sm">No {department}-exclusive features</p>
                    <p className="text-xs mt-1">Add features or move common features here</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredDept.map(f => (
                      <FeatureItem
                        key={f._id}
                        feature={f}
                        type="dept"
                        deptKey={department}
                        style={style}
                        isBusy={isBusy}
                        onRequestMakeCommon={(key, name) => setConfirm({ type: "makeCommon", key, name })}
                        onToggleStatus={handleToggleStatus}
                        onRequestDelete={(key, name) => setConfirm({ type: "delete", key, name })}
                      />
                    ))}
                  </div>
                )}
              </div>

              {showCommon && (
                <div className="px-4 pt-2 pb-4">
                  <div className="flex items-center gap-2 mb-3 mt-2">
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">
                      <Globe size={11} />
                      Common Features (shared)
                    </div>
                    <div className="flex-1 border-t border-dashed border-blue-100" />
                    <span className="text-xs text-gray-400">{filteredCommon.length}</span>
                  </div>
                  <p className="text-xs text-gray-400 mb-3 flex items-center gap-1">
                    <Globe size={10} />
                    Visible in all departments. Use &quot;Move to {department}&quot; to make a feature exclusive here.
                  </p>
                  {filteredCommon.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">No common features found</p>
                  ) : (
                    <div className="space-y-2">
                      {filteredCommon.map(f => (
                        <FeatureItem
                          key={f._id}
                          feature={f}
                          type="common"
                          deptKey={department}
                          style={style}
                          isBusy={isBusy}
                          onRequestMoveToDept={(key, name) => setConfirm({ type: "moveDept", key, name })}
                          onToggleStatus={handleToggleStatus}
                          onRequestDelete={undefined}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs text-gray-400">
            Changes apply immediately to roles in {department}
          </p>
          <button
            onClick={() => navigate(`/admin/feature-departments`)}
            className="flex items-center gap-1.5 text-xs font-medium transition-colors hover:opacity-90"
            style={{ color: BRAND_COLORS.primary }}
          >
            <Settings size={12} />
            Full Feature Manager
            <ExternalLink size={11} />
          </button>
        </div>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl border border-gray-200">
            <div className="flex gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-50">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold text-gray-900">
                  {confirm.type === "makeCommon" && "Share with all departments?"}
                  {confirm.type === "moveDept" && `Make exclusive to ${department}?`}
                  {confirm.type === "delete" && "Delete feature?"}
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  {confirm.type === "makeCommon" && (
                    <>“{confirm.name}” will be visible in <strong>every</strong> department catalog.</>
                  )}
                  {confirm.type === "moveDept" && (
                    <>“{confirm.name}” will be removed from COMMON and assigned only to <strong>{department}</strong>.</>
                  )}
                  {confirm.type === "delete" && (
                    <>Permanently remove “{confirm.name}” ({confirm.key}). This may affect roles using it.</>
                  )}
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirm(null)}
                disabled={isBusy}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={runConfirmedAction}
                disabled={isBusy}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                style={{ background: confirm.type === "delete" ? BRAND_COLORS.error : BRAND_COLORS.primary }}
              >
                {isBusy ? <Loader2 className="h-4 w-4 animate-spin inline" /> : null}
                {confirm.type === "delete" ? "Delete" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

interface FeatureItemProps {
  feature: Feature;
  type: "dept" | "common";
  deptKey: DeptKey;
  style: typeof DEPT_STYLE[DeptKey];
  isBusy: boolean;
  onRequestMakeCommon?: (key: string, name: string) => void;
  onRequestMoveToDept?: (key: string, name: string) => void;
  onToggleStatus: (key: string) => void;
  onRequestDelete?: (key: string, name: string) => void;
}

function FeatureItem({
  feature, type, deptKey, style, isBusy,
  onRequestMakeCommon, onRequestMoveToDept, onToggleStatus, onRequestDelete,
}: FeatureItemProps) {
  const isCommon = type === "common";
  const [techOpen, setTechOpen] = useState(false);

  const cardClass = isCommon
    ? "bg-blue-50/50 border-blue-100 hover:border-blue-200"
    : `${style.bg} ${style.cardBorder}`;

  return (
    <div className={`flex flex-col gap-2 rounded-xl border p-3 transition-all ${cardClass} ${!feature.isActive ? "opacity-50" : ""}`}>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${
          isCommon ? "bg-blue-100 text-blue-700" : `${style.bg} ${style.color}`
        }`}>
          {feature.icon.replace("Icon", "").charAt(0).toUpperCase()}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-800">{feature.name}</span>
            {feature.parentKey && (
              <span className="text-xs text-gray-400">↳ sub-feature</span>
            )}
            {!feature.isActive && (
              <span className="text-xs font-medium text-gray-500">Inactive</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setTechOpen(o => !o)}
            className="mt-1 flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
          >
            <ChevronDown size={14} className={`transition-transform ${techOpen ? "rotate-180" : ""}`} />
            {techOpen ? "Hide technical details" : "Technical details"}
          </button>
          {techOpen && (
            <div className="mt-2 rounded-md bg-white/80 px-2 py-1.5 text-xs font-mono text-gray-600 border border-gray-100 space-y-0.5">
              <div><span className="text-gray-400">key</span> {feature.key}</div>
              <div><span className="text-gray-400">permissionKey</span> {feature.permissionKey}</div>
            </div>
          )}
        </div>

        <div className="flex flex-shrink-0 items-center gap-1">
          <button onClick={() => onToggleStatus(feature.key)} disabled={isBusy}
            title={feature.isActive ? "Deactivate" : "Activate"}
            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
              feature.isActive
                ? "hover:bg-emerald-100 text-emerald-600 border border-transparent hover:border-emerald-200"
                : "hover:bg-gray-100 text-gray-400 border border-gray-200"
            }`}>
            {feature.isActive ? <Eye size={13} /> : <EyeOff size={13} />}
          </button>

          {!isCommon && onRequestMakeCommon && (
            <button onClick={() => onRequestMakeCommon(feature.key, feature.name)} disabled={isBusy}
              title="Make common (all departments)"
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-blue-100 text-blue-600 border border-transparent hover:border-blue-200 transition-colors">
              <Globe size={13} />
            </button>
          )}
          {isCommon && onRequestMoveToDept && (
            <button onClick={() => onRequestMoveToDept(feature.key, feature.name)} disabled={isBusy}
              title={`Move to ${deptKey} only`}
              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors border ${style.bg} ${style.color} hover:opacity-90`}>
              <Plus size={13} />
            </button>
          )}

          {!isCommon && onRequestDelete && (
            <button onClick={() => onRequestDelete(feature.key, feature.name)} disabled={isBusy}
              title="Delete feature"
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-red-100 text-red-500 border border-transparent hover:border-red-200 transition-colors">
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
